/*
projection.js, 2023-04-04, 西岡 芳晴 ( NISHIOKA Yoshiharu )
主にウェブメルカトル座表と緯度経度座標系の変換を行います．
・必要とするライブラリ
　無し
KruegerConverterの変換式は以下の文献をもとに作成しました．
・河瀬(2011)
Gauss-Kruger投影における経緯度座標及び平面直角座標相互間の座標換算についてのより簡明な計算方法
国土地理院時報　2011，No.121, p109-124
（平面直角座標⇒系経緯度変換プログラム例，経緯度変換⇒平面直角座標系プログラム例）
*/
'use strict';

//// 定数
// 旧測地系，ベッセル楕円体(Bessel 1841)，飛田(2002)p101
const bessel1841 = { a:6377397.155, f: 1/299.1528128 };
// 世界測地系楕円体，ITRF座標系GRS80楕円体，飛田(2002)p101
const grs80 = { a: 6378137, f: 1/298.257222101 };
// ウェブメルカトルでの地球半径(GRS80楕円体の長半径）
const webMercatorR = grs80.a;
// ウェブメルカトルにおける緯度の限界
const webMercatorLatLimit = webMercatorToLatLng( { x: 0, y: 0 } ).lat;


//// 緯度経度座標系関連
function latLngRadToDeg( latLngRad ) {
	return { lat: latLngRad.phi / Math.PI * 180, lng: latLngRad.lmbd / Math.PI * 180 };
}

function latLngDegToRad( latLng ) {
	return { phi: latLng.lat / 180 * Math.PI, lmbd: latLng.lng / 180 * Math.PI };
}

//// 3次元直交座標関連
// 緯度経度(ラジアン)を三次元直交座標に変換
// he: 楕円体高さ
function latLngRadToCartesian( latLngRadian, he = 0, ellipsoid = grs80 ){
	const { phi, lmbd } = latLngRadian;
	const { a, f } = ellipsoid;
	const e2 = f * ( 2 - f );
	const n = a / Math.sqrt( 1 - e2 * ( Math.sin( phi ) ** 2 ) );
	const x = ( n + he ) * Math.cos( phi ) * Math.cos( lmbd );
	const y = ( n + he ) * Math.cos( phi ) * Math.sin( lmbd );
	const z = ( n * ( 1 - e2 ) + he ) * Math.sin( phi );
	return { x, y, z };
}

// 三次元直交座標を緯度経度(ラジアン)に変換
function cartesianToLatLngRad( xyz, ellipsoid = grs80 ){
	const { x, y, z } = xyz;
	const { a, f } = ellipsoid;
	const e2 = f * ( 2 - f );
	const f2 = 1 - f;
	const lmbd = Math.atan( y / x ) + ( y / x < 0 ? Math.PI : 0 );
	const p = Math.sqrt( x * x + y * y );
	const r = Math.sqrt( p * p + z * z );
	const mu = Math.atan( z / p * ( f2  + e2 * a / r ) );
	const phi = Math.atan( ( z + e2  * a * Math.sin( mu ) ** 3 / f2 )
			/ ( p - e2 * a * Math.cos( mu ) ** 3 ) );
	const he = p * Math.cos ( phi ) + z * Math.sin( phi )
			- a * Math.sqrt( 1 - e2 * Math.sin( phi ) ** 2 );
	return { phi, lmbd, he };
}

// 緯度経度を三次元直交座標に変換
// he: 楕円体高さ
function latLngToCartesian( latLng, he = 0, ellipsoid = grs80 ){
	return  latLngRadToCartesian( latLngDegToRad( latLng ), he, ellipsoid );
}

// 三次元直交座標を緯度経度に変換
function cartesianToLatLng( xyz, ellipsoid = grs80 ){
	return latLngRadToDeg( cartesianToLatLngRad( xyz, ellipsoid ) );
}

//// ウェブメルカトル座標系関連
function latLngRadToWebMercator( latLngRadian, order = 8 ){
	const
		w = 2 ** ( order - 1 ),
		y0 = Math.log( Math.tan( Math.PI * ( 1 / 4 + latLngRadian.phi / Math.PI / 2 ) ) );

	return { x: ( latLngRadian.lmbd / Math.PI + 1 ) * w, y: ( 1 - y0 / Math.PI ) * w,
			order: order };
}

function webMercatorToLatLngRad( webMer ){
	let w;
	let	yy;

	webMer.order = webMer.order ?? 8;
	w = 2 ** webMer.order;
	yy = Math.atan( Math.pow( Math.E, ( 0.5 - webMer.y / w ) * 2 * Math.PI ) );
	return  { phi : yy * 2 - Math.PI / 2, lmbd: ( webMer.x / w * 2 - 1 ) * Math.PI };
}

function latLngToWebMercator( latLng, order = 8 ){
	return  latLngRadToWebMercator( latLngDegToRad( latLng ), order );
}

function webMercatorToLatLng( webMer ){
	return latLngRadToDeg( webMercatorToLatLngRad( webMer ) );
}

function webMercatorXToLng( x, order = 8 ){
	return x / ( 2 ** order ) * 360 - 180;
}

function webMercatorYToLat( y, order = 8 ){
	return Math.atan( Math.pow( Math.E, ( 0.5 - y / ( 2 ** order ) ) * 2 * Math.PI ) )
			 / Math.PI * 360 - 90;
}

class KruegerConverter{
	constructor( ellipsoid = grs80, m0 = 1 ){
		this.e = [];
		this.alp = [];
		this.beta = [];									// β
		this.dlt = [];									// δ
		this.m0 = m0;
		this.setEllipsoid( ellipsoid );
	}

	setEllipsoid( ellipsoid ){
		const { m0, e, alp, beta, dlt } = this;
		const a = ellipsoid.a;							// 長半径, a
		const rf = 1 / ellipsoid.f;						// 逆扁平率, F
		const n = this.n = 0.5 / ( rf - 0.5 );			// n, 1/(2F-1)
		const anh = this.anh = 0.5 * a / ( 1 + n );		// 作業用 a/2(1+n)
		const n15 =1.5 * n;								// 作業用 3/2n
		const nsq = n * n;								// 作業用 n^2
		const jt = this.jt = 5;							// 項数？

		this.ep = 1;
		this.ra = 2 * anh * m0 * ( 1 + nsq / 4 + nsq * nsq / 64 );	// Ā
		this.jt2 = 2 * jt;				// 作業用 2・jt
		for( let k = 1; k <= jt; k++ ) {
			this.ep *= e[ k ] = n15 / k - n;
			e[ k + jt ] = n15 / ( k + jt ) - n;
		}
		alp[ 1 ] = ( 1/2 + ( -2/3 + ( 5/16 + ( 41 / 180 - 127 / 288 * n ) * n ) * n ) * n ) * n;
		alp[ 2 ] = ( 13 / 48 + ( -3 / 5 + ( 557 / 1440 + 281 / 630 * n ) * n ) * n ) * nsq;
		alp[ 3 ] = ( 61 / 240 + ( -103 / 140 + 15061 / 26880 * n ) * n ) * n * nsq;
		alp[ 4 ] = ( 49561 / 161280 - 179 / 168 * n ) * nsq * nsq;
		alp[ 5 ] = 34729 / 80640 * n * nsq * nsq;
		beta[ 1 ] = ( 1/2 +( -2/3 + ( 37/96 + ( -1 / 360 - 81 / 512 * n ) * n ) * n ) * n ) * n;
		beta[ 2 ] = ( 1 / 48 +( 1 / 15 +( -437 / 1440 + 46 / 105 * n ) * n ) * n ) * nsq;
		beta[ 3 ] = ( 17 / 480 + ( -37 / 840 - 209 / 4480 * n ) * n ) * n * nsq;
		beta[ 4 ] = ( 4397 / 161280 - 11 / 504 * n ) * nsq * nsq;
		beta[ 5 ] = 4583 / 161280 * n * nsq * nsq;
		dlt[ 1 ] = ( 2+( -2/3+ ( -2+( 116/45+( 26/45-2854/675 * n ) * n ) * n ) * n ) * n ) * n;
		dlt[ 2 ] = ( 7/3+( -8/5+ ( -227/45 + ( 2704/315 + 2323/945 * n ) * n ) * n ) * n ) *nsq;
		dlt[ 3 ] = ( 56 / 15 + ( -136/35 + ( -1262/105 + 73814/2835 * n ) * n ) * n ) * n * nsq;
		dlt[ 4 ] = ( 4279 / 630 + ( -332 / 35 - 399572 / 14175 * n ) * n ) * nsq * nsq;
		dlt[ 5 ] = ( 4174 / 315 - 144838 / 6237 * n ) * n * nsq * nsq;
		dlt[ 6 ] = 601676 / 22275 * nsq * nsq * nsq;
	}
	// 該当緯度の2倍角の入力により赤道からの子午線弧長を求める関数
	merid( phi2 ) {
		const { anh, e, ra, jt, jt2 } = this;
		const s = [ 0 ];
		const dc = 2 * Math.cos( phi2 );
		const t = [];
		let sum = 0;
		let c1 = this.ep;
		let j = jt;

		s[ 1 ] = Math.sin( phi2 );
		for( let i = 1; i <= jt2; i++) {
			 s[ i + 1 ] = dc * s[ i ] - s[ i - 1 ] ;
			 t[ i ] = ( 1 / i - 4 * i ) * s[ i ]
		}
		while( j ) {
			let c2 = phi2;
			let c3 = 2;
			let l = j;

			while( l ) {
				const m =( j - l ) * 2 + 1;

				c2 += (c3 /= e[ l-- ] ) * t[ m ] + ( c3 *= e[ 2 * j - l ] ) * t[ m + 1 ] ;
			}
			sum += c1 * c1 * c2;
			c1 /= e[ j-- ]
		}
		return anh * ( sum + phi2 )
	}

	toLatLngRad( kruegerCode, phi0, lmbd0, ellipsoid ){
		( ellipsoid ) ? this.setEllipsoid( ellipsoid ) : '';
		const { m0, beta, ra, dlt } = this;
		const xi = ( kruegerCode.northing + m0 * this.merid( 2 * phi0 ) ) / ra;	// ε
		const eta = kruegerCode.easting / ra;													// η
		let	xip = xi;															// ε'
		let etap = eta;															// η'
		let chi;																// χ
		let phi;																// φ
		let lmbd;																// λ

		for( let j = beta.length; --j; ){
			xip  -= beta[j] * Math.sin( 2 * j * xi ) * Math.cosh( 2 * j * eta );
			etap -= beta[j] * Math.cos( 2 * j * xi ) * Math.sinh( 2 * j * eta );
		}
		phi = chi = Math.asin( Math.sin( xip ) / Math.cosh( etap ) )
		for( let j = dlt.length; --j; ) {
			phi += dlt[ j ] * Math.sin( 2 * j * chi )
		}
		lmbd= lmbd0 + Math.atan2( Math.sinh( etap ), Math.cos( xip ) );
		return { phi, lmbd }
	}

	fromLatLngRad( latLngRad, phi0, lmbd0, ellipsoid ){
		( ellipsoid ) ? this.setEllipsoid( ellipsoid ) : '';
		const { m0, n, alp, ra } = this;
		const sphi = Math.sin( latLngRad.phi );
		const dlmbd= latLngRad.lmbd - lmbd0;
		const e2n = 2 * Math.sqrt( n ) / ( 1 + n );
		const tchi = Math.sinh( Math.atanh( sphi ) - e2n * Math.atanh( e2n * sphi ) );
		const xip = Math.atan2( tchi, Math.cos( dlmbd ) );

		let xi = xip;
		let etap = Math.atanh( Math.sin( dlmbd ) / Math.sqrt( 1 + tchi * tchi ) );
		let eta = etap;
		let sgm = 1 ;
		let tau = 0
		for( let j = alp.length; --j; ){
			const alsin = alp[ j ] * Math.sin( 2 * j * xip );
			const alcos = alp[ j ] * Math.cos( 2 * j * xip );
			xi += alsin * Math.cosh( 2 * j * etap );
			eta += alcos * Math.sinh( 2 * j * etap );
			sgm += 2 * j * alcos * Math.cosh( 2 * j * etap );
			tau += 2 * j * alsin * Math.sinh( 2 * j * etap );
		}
		return { northing: ra * xi- m0 * this.merid( 2 * phi0 ), easting: ra * eta };
	}
}

// 以下2023-04-04以前の互換性のために保存，廃止予定
const webMelR = webMercatorR;
const webMelLatLimit = webMercatorLatLimit;
const latLngRadToWebMel = latLngRadToWebMercator;
const latLngToWebMel = latLngToWebMercator;
const webMelToLatLngRad = webMercatorToLatLngRad;
const webMelToLatLng = webMercatorToLatLng
const webMelXToLng = webMercatorXToLng;
const webMelYToLat = webMercatorYToLat;

export {
	grs80, bessel1841, webMercatorR, webMercatorLatLimit,
	latLngRadToCartesian, cartesianToLatLngRad, latLngToCartesian, cartesianToLatLng,
	latLngRadToDeg, latLngDegToRad,
	latLngRadToWebMercator, webMercatorToLatLngRad, latLngToWebMercator, webMercatorToLatLng,
	webMercatorXToLng, webMercatorYToLat, KruegerConverter,

	// 以下2023-04-04以前の互換性のために保存，廃止予定
	webMelR, webMelLatLimit,
	latLngRadToWebMel, webMelToLatLngRad, latLngToWebMel, webMelToLatLng,
	webMelXToLng, webMelYToLat
};
